import {DataTableColumn} from "mantine-datatable";
import {CustomerSubscription} from "../types.ts";
import {Text, Button, Group} from '@mantine/core';
import {Link} from 'react-router-dom';
import {SubscriptionStatusBadge} from '../profile/SubscriptionStatusBadge.tsx';
import {activateRecord, cancelRecord} from '../services/CustomerSubscriptionsService.ts';
import {useQueryClient} from '@tanstack/react-query';
import TableFormattedTimestamp from '../../../utils/TableFormattedTimestamp.tsx';

export const useTableColumns = (context: 'customer' | 'reports' = 'reports') => {
    const queryClient = useQueryClient();

    const handleActivate = async (id: string) => {
        await activateRecord(id);
        queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
    };

    const handleCancel = async (id: string) => {
        await cancelRecord(id);
        queryClient.invalidateQueries({ queryKey: ['customer-subscriptions'] });
    };

    const columns: DataTableColumn<CustomerSubscription>[] = [
        {
            accessor: 'items',
            title: 'Subscriptions',
            render: (record: CustomerSubscription) => {
                const items = Array.isArray(record.items?.items) ? record.items.items : [];
                
                if (items.length === 0) {
                    return (
                        <Link to={`/customers/${record.customerId}/subscriptions/${record.id}`} style={{ textDecoration: 'none' }}>
                            <Text c="blue" style={{ cursor: 'pointer' }} size="sm">
                                Empty subscription
                            </Text>
                        </Link>
                    );
                }
                
                return (
                    <Link to={`/customers/${record.customerId}/subscriptions/${record.id}`} style={{ textDecoration: 'none' }}>
                        <div style={{ cursor: 'pointer' }}>
                            {items.slice(0, 2).map((item: any, index: number) => (
                                <Text key={index} size="sm" c="blue">
                                    {item.price?.product?.name || `Price ${item.priceId}`} (x{item.quantity})
                                </Text>
                            ))}
                            {items.length > 2 && <Text size="xs" c="dimmed">+{items.length - 2} more</Text>}
                        </div>
                    </Link>
                );
            }
        },
        ...(context === 'reports' ? [{
            accessor: 'customer',
            title: 'Customer',
            render: (record: CustomerSubscription) => (
                <Link to={`/customers/${record.customerId}`} style={{ textDecoration: 'none' }}>
                    <Text c="blue" style={{ cursor: 'pointer' }}>
                        {record.customer ? record.customer.name : record.customerId}
                    </Text>
                </Link>
            )
        }] : []),
        {
            accessor: 'status',
            title: 'Status',
            render: (record: CustomerSubscription) => (
                <SubscriptionStatusBadge record={record} />
            )
        },
        {
            accessor: 'created',
            title: 'Created',
            render: (record: CustomerSubscription) => (
                <TableFormattedTimestamp record={record} propertyName="created" />
            )
        },
        {
            accessor: 'actions',
            title: 'Actions',
            render: (record: CustomerSubscription) => (
                <Group gap="xs">
                    {record.status === 'Inactive' && (
                        <Button size="xs" onClick={() => handleActivate(record.id)}>
                            Activate
                        </Button>
                    )}
                    {record.status === 'Active' && (
                        <Button size="xs" color="red" onClick={() => handleCancel(record.id)}>
                            Cancel
                        </Button>
                    )}
                </Group>
            )
        },
    ];

    return {
        columns,
    }
}